import { useSuspenseQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { NewProduct } from '@/components/admin/new-product';
import { Pager } from '@/components/pager';
import { usePagedList } from '@/lib/use-paged-list';
import { Badge, Callout, EmptyState, inputClass } from '@/components/ui';
import { adminProductListingQuery, adminShellQuery } from '@/queries';
import { DownloadCsv } from '@/components/admin/download-csv';
import type { ProductListing } from '@/lib/stock-types';

/**
 * The product record: what the shop says about a style. D021.
 *
 * Separate from Publishing, which composes a shop listing from a blank and a
 * design. This is the style itself, and what a shopper reads about it.
 *
 * A row opens the style's own page (spec 010), which holds everything about
 * it: the words and photos, its colours and prices, and its stock by size.
 */

/**
 * One line in the list. It opens the style's page and does nothing else.
 *
 * It opened a panel over the list until spec 010. The panel held the words and
 * photos only, so the colours, prices and stock of the same style were on two
 * other screens, which is what Guy asked to bring together (tracker #10).
 */
function ProductRow({ product }: { product: ProductListing }) {
  return (
    <div className="border-b border-line-soft last:border-b-0">
      <Link
        to="/admin/styles/$productId"
        params={{ productId: product.id }}
        className="flex w-full items-center gap-3 px-[14px] py-2 text-left hover:bg-raised"
      >
        <span className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-[4px] border border-line bg-raised">
          {product.product_images[0]
            ? <img src={product.product_images[0].url} alt="" className="h-8 w-8 object-cover" />
            : <span className="font-[family-name:var(--font-mono)] text-[9px] text-faint">NO IMG</span>}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[13px] font-medium">{product.name}</span>
          <span className="block truncate text-[11.5px] text-muted">
            {product.category} &middot; {product.product_variants.length} variants
            {product.description ? '' : ' · no description'}
          </span>
        </span>
        {(product.tags ?? []).map((t) => (
          <Badge key={t} tone="neutral">{t}</Badge>
        ))}
        <span className="font-[family-name:var(--font-mono)] text-[11px] text-muted">
          {product.product_images.length} img
        </span>
      </Link>
    </div>
  );
}

export default function AdminProductListingPage() {
  // A request per keystroke against 380 products with their variants embedded
  // is a load test with a text box in front of it, so the hook debounces. R-272.
  const list = usePagedList<ProductListing>(
    ({ page, q }) => adminProductListingQuery({ page, q }) as never,
  );
  const { setPage, query, setQuery, needle, isFetching } = list;
  const data = list.data as { incomplete?: number } | undefined;

  const products = list.rows;

  /**
   * May this person add one? `catalogue.create`, asked of the permission
   * matrix: admin and manager hold it, staff do not. Already in the cache,
   * because the admin shell loads it.
   */
  const { can } = useSuspenseQuery(adminShellQuery).data;
  const mayCreate = (can.catalogue ?? []).includes('create');
  // Counted by the database over every product, not by this page over fifty of
  // them. "12 of 50 are not ready" and "12 of 380 are not ready" are different
  // statements and only one of them was true.
  const missing = data?.incomplete ?? 0;

  return (
    <div>
      <div className="mb-[14px] flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Products</h1>
          <p className="mt-1 text-[12.5px] text-muted">
            The catalogue. Add a style here, or open one to change its words and
            photos, its colours, sizes and prices, and to see its stock.
          </p>
        </div>
        {/* Every product until now arrived through the Shopify export. The
            catalogue lives in this system, so it has to be able to hold a
            garment Shopify has never seen. R-298. */}
        {mayCreate && <NewProduct />}
      </div>

      {missing > 0 && (
        <Callout
          tone="warn"
          title={`${missing} of ${list.pager.total} ${missing === 1 ? 'product has' : 'products have'} no description`}
          className="mb-5"
        >
          A shopper sees a grey box and a name.
        </Callout>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search a product name"
          aria-label="Search products"
          className={`${inputClass} min-w-[280px] flex-1`}
        />
        {isFetching && <span className="text-[13px] text-muted">Loading...</span>}
        <DownloadCsv path="/api/products/export" params={{ q: needle.trim() }} />
      </div>

      {products.length === 0 && needle ? (
        <EmptyState
          title="Nothing matches that"
          body="The search covers every product name in the catalogue, not only the
                ones on this page."
        />
      ) : products.length === 0 ? (
        <EmptyState
          title="No products yet"
          body="Products arrive with the catalogue export. Once they are loaded, this
                is where the words and the photographs are added."
        />
      ) : (
        <div className="overflow-hidden rounded-[10px] border border-line bg-panel shadow-[var(--shadow-1)]">
          {products.map((p) => (
            <ProductRow key={p.id} product={p} />
          ))}
          <Pager
            {...list.pager}
            noun="product"
            onPage={(n) => { setPage(n); window.scrollTo({ top: 0 }); }}
          />
        </div>
      )}

    </div>
  );
}
